import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { MessageService } from 'primeng/api';
import { CircleCreditService } from '../../data/services/view/circle-credit.service';
import { LeaseDetailService } from '../../data/services/view/lease-detail.service';
import { EntityType } from '../../data/interfaces/circle-credit.interface';

@Component({
  selector: 'app-lease-circle-credit',
  templateUrl: './lease-circle-credit.component.html',
  styleUrls: ['./lease-circle-credit.component.scss'],
  providers: [MessageService]
})
export class LeaseCircleCreditComponent implements OnInit {
  
  // Nueva propiedad para manejar estado de descarga
  isDownloading = false;
  
  constructor(
    public circleCreditService: CircleCreditService,
    private leaseDetailService: LeaseDetailService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  // Getters públicos para el template
  get creditCircle() {
    return this.leaseDetailService.data?.creditCircle;
  }

  get hasCompany() {
    return this.creditCircle?.company?.companyName;
  }

  get hasCredits() {
    return this.creditCircle?.credits?.length;
  }

  get grantorDisplayName() {
    if (this.clientEntity === 'Física') {
      return this.creditCircle?.header?.grantorName || this.creditCircle?.header?.grantorKey;
    } else {
      return this.creditCircle?.grantorName || this.creditCircle?.grantor;
    }
  }

  get companyName() {
    return this.creditCircle?.company?.companyName;
  }

  get creditsCount() {
    return this.creditCircle?.credits?.length || 0;
  }

  // Getters específicos para PM
  get hasShareholder() {
    return this.creditCircle?.shareholder?.companyName || this.creditCircle?.shareholder?.firstName;
  }

  get hasGuarantor() {
    return this.creditCircle?.guarantor?.companyName || this.creditCircle?.guarantor?.firstName;
  }

  get shareholderDisplayName() {
    const shareholder = this.creditCircle?.shareholder;
    if (!shareholder) return '';
    
    return shareholder.companyName || 
           `${shareholder.firstName || ''} ${shareholder.lastNameFatherShareholder || ''}`.trim();
  }

  get guarantorDisplayName() {
    const guarantor = this.creditCircle?.guarantor;
    if (!guarantor) return '';
    
    return guarantor.companyName || 
           `${guarantor.firstName || ''} ${guarantor.lastNameFatherGuarantor || ''}`.trim();
  }

  // Métodos de utilidad para mostrar información
  get isPMEntity(): boolean {
    return this.clientEntity === 'Moral';
  }

  get isPFEntity(): boolean {
    return this.clientEntity === 'Física';
  }

  ngOnInit(): void {
    // Componente inicializado
  }

  get clientEntity(): EntityType {
    const type = this.leaseDetailService.data?.creditCircleType;
    return type === 'PFFormat' ? 'Física' : 'Moral';
  }

  get hasCircleCredit(): boolean {
    return !!this.leaseDetailService.data?.creditCircle;
  }

  onEditCircleCredit() {
    const leaseData = this.leaseDetailService.data;
    
    if (leaseData?.creditCircle && leaseData?.creditCircleType) {
      this.circleCreditService.openEditModal(
        leaseData.creditCircle,
        leaseData.creditCircleType
      );
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Información no disponible',
        detail: 'No hay información de círculo de crédito para editar'
      });
    }
  }

  // Método para manejar el guardado pasando el MessageService local
  onSaveCircleCredit() {
    this.circleCreditService.saveCircleCreditData(this.messageService);
    
    // Forzar detección de cambios después de un pequeño delay
    // para asegurar que los datos se actualicen en el template
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  getCircleCreditStatus(): string {
    const creditCircle = this.leaseDetailService.data?.creditCircle;
    
    if (!creditCircle) {
      return 'Sin información';
    }
    
    const hasRequiredData = this.clientEntity === 'Física' 
      ? (creditCircle as any).header?.grantorKey
      : (creditCircle as any).grantor;
    
    return hasRequiredData ? 'Información disponible' : 'Información incompleta';
  }

  getCircleCreditStatusClass(): string {
    const creditCircle = this.leaseDetailService.data?.creditCircle;
    
    if (!creditCircle) {
      return 'status-empty';
    }
    
    const hasRequiredData = this.clientEntity === 'Física' 
      ? (creditCircle as any).header?.grantorKey
      : (creditCircle as any).grantor;
    
    return hasRequiredData ? 'status-complete' : 'status-incomplete';
  }

  /**
   * Descarga el reporte de círculo de crédito en formato Excel
   */
  downloadReport(): void {
    const creditCircleId = this.creditCircle?._id;
    
    if (!creditCircleId) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atención', 
        detail: 'No se puede descargar el reporte sin ID del círculo de crédito' 
      });
      return;
    }

    this.isDownloading = true;
    this.circleCreditService.downloadReport(creditCircleId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const today = new Date().toISOString().split('T')[0];
        const entityType = this.clientEntity === 'Física' ? 'PF' : 'PM';
        a.download = `reporte_circulo_credito_${entityType}_${today}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        this.messageService.add({ 
          severity: 'success', 
          summary: 'Éxito', 
          detail: 'Reporte descargado correctamente' 
        });
        this.isDownloading = false;
      },
      error: (error) => {
        console.error('Error al descargar reporte:', error);
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'No se pudo descargar el reporte' 
        });
        this.isDownloading = false;
      }
    });
  }
}
